import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute } from '@angular/router';

import {
  Actions,
  Department,
  GlobalApplicationFields,
  Workflow,
  WorkflowType
} from '../../../../models';
import { DepartmentAddress } from '../../../../models/address';
import { DepartmentPhoneNumber } from '../../../../models/phone-number';
import { ClientService, Utilities, WorkflowContextService, WorkflowService, SecurityService } from '../../../../services';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import {
  ItemSearchOptionField,
  ItemSearchOptionFieldOption
} from 'src/app/components/filter-list/models/filterClasses';
import {
  DataSetField,
  FilterConfig,
  ReportDataSetFieldDataType,
  WorkflowFilter
} from 'src/app/models/custom-report';
import {
  FilterBuilderFilter,
  FilterBuilderFilterFlat,
  FilterBuilderParam
} from 'src/app/models/filter-builder';
import {
  DataSet,
  ReportDataSetOrder,
  SortDirection,
  WorkflowApplicationReportDataSet
} from 'src/app/models/report-data-set';
import { DateDataEntity, FreeFormTextDataEntity } from 'src/app/models/data-entities';
import { CommonDynamicTableComponent } from 'src/app/components/common/common-dynamic-table/common-dynamic-table.component';
import { TableColumn as CommonTableColumn } from 'src/app/components/common/common-table/common-table.component';
import { environment } from '../../../../../environments/environment';

interface Column {
  id: string;
  name: string;
  resultHeader: string;
  dataType: number;
  mappings: any[];
  outputFormula: any;
  conditionalOutput: boolean;
  // Added by Amisha on 05/21/2026 so the shared Janus table can carry forward
  // legacy-style protected column behavior without requerying the backing field.
  isStatic?: boolean;
}

interface WorkflowRow {
  rowKey: string;
  workflow: string;
  workflowId?: string | null;
  [key: string]: any;
  workflowFilterConfig?: WorkflowFilter | null;
  filterParams?: FilterBuilderParam[];
  filters?: any[];
  filterBuilderId?: string;
}

@Component({
  selector: 'wm-settings-departments',
  standalone: false,
  templateUrl: './settings-departments.component.html',
  styleUrls: ['./settings-departments.component.scss']
})
export class SettingsDepartmentsComponent implements OnInit, OnDestroy {
  Utilities = Utilities;
  Object = Object; 
  
  @ViewChild('settingsContainer') settingsContainer?: ElementRef<HTMLElement>;
  
  @ViewChild(CommonDynamicTableComponent)
  commonDynamicTable?: CommonDynamicTableComponent;
    // Added by Amisha on 04/24/2026: capture the dataset card and table anchor so the shared column settings overlay can be positioned relative to the visible department dataset section.
  @ViewChild('datasetDesignerCard')
  datasetDesignerCard?: ElementRef<HTMLElement>;
  @ViewChild('commonDynamicTableAnchor')
  commonDynamicTableAnchor?: ElementRef<HTMLElement>;

  showInlineEditor = false;
  isEditMode = false;
  isSaving = false;
  canConfigureInspectionApp = false;
  isLoading = false;
  isFetchingDetails = false;
  isDatasetLoading = false;

  showIconMenu = false;

  form: FormGroup;
  editingId: string | null = null;
  currentEditingDept: Department | null = null;
  clientSub$: Subscription;
  ds: DataSet | null = null;
  dataSetConfig: WorkflowApplicationReportDataSet | null = null;
  rows: WorkflowRow[] = [];
  columns: Column[] = [];
  availableWorkflows: Workflow[] = [];
  cellDropdownEntities: any[] = [];
  selectedColumnSettingsWorkflowId: string | null = null;
  selectedColumnSettingsField: any | null = null;
  columnSettingsTopOffsetPx = 0;
  isColumnSettingsOpen = false;
  // Added by Amisha on 04/24/2026: track report-style dataset actions and test results locally in Settings Departments while reusing the common dynamic table.
  isTestingDataSet = false;
  isSavingDataSetDraft = false;
  isPublishingDataSet = false;
  resultColumns: CommonTableColumn[] = null;
  resultRows: any[] = null;
  workflowDisplayNameResolver = (
    workflowId?: string | null,
    fallback = 'Select Workflow'
  ) => this.getWorkflowDisplayName(workflowId, fallback);
  private workflowFilterParamsCache: {
    [workflowId: string]: FilterBuilderParam[];
  } = {};
  private workflowRowKeys: string[] = [];

  departments: Department[] = [];

  // API Testing Controls Properties
  showTestingControls$: Observable<boolean>;
  testingApi = false;
  testingFilter: string | null = null;
  testingSortField: string | null = null;
  testingCustomFilter: boolean = false;
  testingSimpleSearch: boolean = false;

  apiTestResults: any = null;
  apiTestResultColumns: any[] = [];
  apiTestResultRows: any[] = [];
  apiTestResultRowsOriginal: any[] = [];
  lastApiTestDescription: string = '';

  gridSortColumn: string | null = null;
  gridSortDescending: boolean = false;
  gridFilters: { [key: string]: string } = {};

  customFilterField: string = '';
  customFilterValue: string = '';
  customFilterOperator: string = 'contains';
  availableFilterFields: any[] = [];
  simpleSearchValue: string = '';

  availableColumns: Array<{name: string; label: string}> = [];
  selectedColumns: string[] = [];
  selectAllColumns: boolean = true;

  filterOperators = [
    { value: '==', label: 'Equals (=)' },
    { value: '!=', label: 'Not Equals (≠)' },
    { value: 'contains', label: 'Contains' },
    { value: 'startswith', label: 'Starts With' },
    { value: 'endswith', label: 'Ends With' },
    { value: '>', label: 'Greater Than (>)' },
    { value: '>=', label: 'Greater Than or Equal (≥)' },
    { value: '<', label: 'Less Than (<)' },
    { value: '<=', label: 'Less Than or Equal (≤)' }
  ];

  sortableColumns = [
    { name: 'APPLICATION_NUMBER', label: 'Application #' },
    { name: 'APPLICATION_STATUS', label: 'Status' },
    { name: 'PERMIT_DESCRIPTION', label: 'Description' },
    { name: 'SUBMITTER', label: 'Submitter' },
    { name: 'CreateDate', label: 'Create Date' },
    { name: 'IssuedOn', label: 'Issued Date' },
    { name: 'WORKFLOW_NAME', label: 'Workflow Name' }
  ];

  confirmData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: true,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };
  pendingDeleteDept: Department | null = null;
  // Added by Amisha on 04/24/2026: reuse the publish confirmation pattern from report dataset screens in department settings.
  publishDataSetModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Publish Data Set Confirmation',
    modalSubDescp: 'Are you sure you want to publish the changes to this Data Set?',
    yesNoBtn: true,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };

  availableIcons = [
    { value: '', label: 'No Icon' },
    { value: 'business', label: 'Building' },
    { value: 'map', label: 'Map/Planning' },
    { value: 'gavel', label: 'Code Enforcement' },
    { value: 'construction', label: 'Construction' },
    { value: 'local_fire_department', label: 'Fire Department' },
    { value: 'security', label: 'Safety/Security' },
    { value: 'eco', label: 'Environmental' },
    { value: 'add_road', label: 'Public Works' },
    { value: 'groups', label: 'Human Resources' },
    { value: 'calculate', label: 'Finance' },
    { value: 'assignment', label: 'Licensing' },
    { value: 'park', label: 'Parks & Recreation' },
    { value: 'directions_car', label: 'Transportation' },
    { value: 'handyman', label: 'Utilities' },
    { value: 'home', label: 'Housing' },
    { value: 'school', label: 'Education' },
    { value: 'health_and_safety', label: 'Health Services' },
    { value: 'balance', label: 'Legal/Court' },
    { value: 'settings', label: 'General Services' },
    { value: 'pets', label: 'Dog Control' }
  ];

  constructor(
    private fb: FormBuilder,
    private _clientService: ClientService,
    private _context: WorkflowContextService,
    private _workflowService: WorkflowService,
    private _toastr: ToastrService,
    private _securityService: SecurityService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      icon: [''],
      enabled: [true],
      requireDocumentReview: [false],
      address1: [''],
      address2: [''],
      city: [''],
      state: [''],
      zip: [''],
      phone: [''],
      fax: ['']
    });
  }

  ngOnInit(): void {
    if (history.state && history.state.departments) {
      this.departments = history.state.departments;
    }

    if (this.departments.length === 0) {
      if (this._context.client) {
        this.loadDepartments();
      } else {
        this.clientSub$ = this._context.client$.subscribe(() => {
          this.loadDepartments();
        });
      }
    }

    this.showTestingControls$ = this._securityService.isSystemAdministrator().pipe(
      map(isSystemAdmin => !environment.production && isSystemAdmin)
    );

    this._securityService.isLoginEntitled(Actions.CONFIGURE_INSPECTION_APP).subscribe(
      canConfigure => this.canConfigureInspectionApp = canConfigure
    );

    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id === 'add') {
        this.setupNewEditor();
      } else if (id) {
        this.fetchAndSetupEditEditor(id);
      } else {
        this.closeEditorInternal();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.clientSub$) {
      this.clientSub$.unsubscribe();
    }
  }

  loadDepartments() {
    this.isLoading = true;
    this._clientService
      .getClientDepartments(this._context.client.id, true)
      .subscribe({
        next: (departments) => {
          this.departments = departments;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load departments:', error);
          this._toastr.error('Failed to load departments');
          this.isLoading = false;
        }
      });
  }

  openNewEditor() {
    if (this.route.snapshot.paramMap.has('id')) {
      this.router.navigate(['../add'], { relativeTo: this.route, state: { departments: this.departments } });
    } else {
      this.router.navigate(['add'], { relativeTo: this.route, state: { departments: this.departments } });
    }
  }

  openEditEditor(dept: Department) {
    if (this.route.snapshot.paramMap.has('id')) {
      this.router.navigate(['../', dept.id], { relativeTo: this.route, state: { departments: this.departments } });
    } else {
      this.router.navigate([dept.id], { relativeTo: this.route, state: { departments: this.departments } });
    }
  }

  closeInlineEditor() {
    if (this.route.snapshot.paramMap.has('id')) {
      this.router.navigate(['../'], { relativeTo: this.route, state: { departments: this.departments } });
    } else {
      this.closeEditorInternal();
    }
  }

  private setupNewEditor() {
    this.isEditMode = false;
    this.editingId = null;
    this.currentEditingDept = null;
    this.form.reset({
      name: '', description: '', icon: '', enabled: true, requireDocumentReview: false,
      address1: '', address2: '', city: '', state: '', zip: '', phone: '', fax: ''
    });
    this.showInlineEditor = true;
    this.showIconMenu = false;
    this.clearApiTestResults();
    this.scrollToEditor();
  }

  private fetchAndSetupEditEditor(id: string) {
    this.isFetchingDetails = true;

    this._clientService.getClientDepartment(id).subscribe({
      next: (fullDept) => {
        this.isFetchingDetails = false;
        this.isEditMode = true;
        this.editingId = fullDept.id;
        this.currentEditingDept = fullDept;

        this.form.patchValue({
          name: fullDept.name,
          description: fullDept.description,
          icon: fullDept.iconUrl || '',
          enabled: fullDept.isEnabled,
          requireDocumentReview: fullDept.requireDocumentReview || false,
          address1: fullDept.address?.address1 || '',
          address2: fullDept.address?.address2 || '',
          city: fullDept.address?.city || '',
          state: fullDept.address?.state || '',
          zip: fullDept.address?.zip || '',
          phone: fullDept.phoneNumber?.number || '',
          fax: fullDept.faxNumber?.number || ''
        });

        this.initializeDepartmentDataSet();
        this.showInlineEditor = true;
        this.showIconMenu = false;
        this.clearApiTestResults();
        this.scrollToEditor();
      },
      error: (error) => {
        this.isFetchingDetails = false;
        console.error('Failed to load department details:', error);
        this._toastr.error('Failed to load department details');
        this.closeInlineEditor();
      }
    });
  }

  private closeEditorInternal() {
    this.showInlineEditor = false;
    this.showIconMenu = false;
    this.form.reset();
    this.editingId = null;
    this.currentEditingDept = null;
    this.ds = null;
    this.dataSetConfig = null;
    this.rows = [];
    this.columns = [];
    this.availableWorkflows = [];
    this.cellDropdownEntities = [];
    this.selectedColumnSettingsWorkflowId = null;
    this.isColumnSettingsOpen = false;
    this.workflowFilterParamsCache = {};
    this.workflowRowKeys = [];
    this.isDatasetLoading = false;
    this.clearApiTestResults();
  }

  scrollToEditor() {
    setTimeout(() => {
      const container = this.settingsContainer?.nativeElement;
      let scrolled = false;

      if (container) {
        let parent = container.parentElement;
        while (parent && parent !== document.body && parent !== document.documentElement) {
          const style = window.getComputedStyle(parent);
          if (
            style.overflowY === 'auto' || 
            style.overflowY === 'scroll' || 
            style.overflow === 'auto' || 
            style.overflow === 'scroll'
          ) {
            parent.scrollTo({ top: 0, behavior: 'smooth' });
            scrolled = true;
            break;
          }
          parent = parent.parentElement;
        }
      }
      if (!scrolled) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        const mainContent = document.querySelector('.mat-drawer-content') || 
                            document.querySelector('.mat-sidenav-content');
        if (mainContent) {
          mainContent.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 50);
  }

  toggleIconMenu() {
    this.showIconMenu = !this.showIconMenu;
  }

  closeIconMenu() {
    this.showIconMenu = false;
  }

  selectIcon(value: string) {
    this.form.patchValue({ icon: value });
    this.closeIconMenu();
  }

  getSelectedIconLabel(): string {
    const val = this.form.get('icon')?.value;
    const found = this.availableIcons.find(i => i.value === val);
    return found ? found.label : 'No Icon';
  }

  get isNoData() {
    return !this.dataSetConfig || this.dataSetConfig.workflowIds.length === 0;
  }

  get hasFewData() {
    const validWorkflowIds = this.dataSetConfig?.workflowIds?.filter(w => w !== '') || [];
    return validWorkflowIds.length > 0 && validWorkflowIds.length <= 2;
  }

  canManageDepartmentDataSet(): boolean {
    return !!this.currentEditingDept?.id && !!this.ds && !!this.dataSetConfig;
  }

  canTestDepartmentDataSet(): boolean {
    return (
      this.canManageDepartmentDataSet() &&
      (this.dataSetConfig?.fields?.length || 0) > 0 &&
      (this.dataSetConfig?.workflowIds || []).some(workflowId => !!workflowId)
    );
  }

  canPublishDepartmentDataSet(): boolean {
    return this.canTestDepartmentDataSet();
  }

  getStaticFiltersForDepartment(
    departmentId: string | null = this.currentEditingDept?.id || null
  ): ItemSearchOptionField[] {
    if (!departmentId) {
      return [];
    }

    return [{
      title: 'DEPARTMENT_ID',
      options: [{
        title: 'DEPARTMENT_ID',
        selected: true,
        filterText: departmentId,
        searchText: departmentId,
        strOperator: 'eq'
      }]
    }];
  }

  getWorkflowsForDepartment = (): Observable<Workflow[]> => {
    return this._workflowService.getWorkflows(
      this._context.client,
      false,
      false,
      [
        WorkflowType.ContractorRegistration,
        WorkflowType.Renewable,
        WorkflowType.ContractorRenewal,
        WorkflowType.Permit,
        WorkflowType.Other
      ],
      this.currentEditingDept?.id
    );
  };

  getStaticFieldsForDepartment(): DataSetField[] {
    const fields: DataSetField[] = [];

    fields.push(...this.getInitialFieldsForDepartment());

    const documentsField = new DataSetField();
    documentsField.name = GlobalApplicationFields.DOCUMENTS_TO_REVIEW;
    documentsField.resultHeader = 'DocumentsToReview';
    documentsField.dataType = ReportDataSetFieldDataType.Integer;
    documentsField.isStatic = true;
    // Added by Amisha on 05/22/2026 to keep only the starter department
    // columns available by default while still retaining system fields in the dataset.
    documentsField.isAvailableColumn = false;
    fields.push(documentsField);

    // Total document count field (drives the blue paperclip icon in the staff
    // applications grid when non-review documents have been uploaded).
    const totalDocumentCountField = new DataSetField();
    totalDocumentCountField.name = GlobalApplicationFields.TOTAL_DOCUMENT_COUNT;
    totalDocumentCountField.resultHeader = 'TotalDocumentCount';
    totalDocumentCountField.dataType = ReportDataSetFieldDataType.Integer;
    totalDocumentCountField.isStatic = true;
    totalDocumentCountField.isAvailableColumn = false;
    fields.push(totalDocumentCountField);

    const messagesField = new DataSetField();
    messagesField.name = GlobalApplicationFields.MESSAGES;
    messagesField.resultHeader = 'Messages';
    messagesField.dataType = ReportDataSetFieldDataType.Integer;
    messagesField.isStatic = true;
    messagesField.isAvailableColumn = false;
    fields.push(messagesField);

    const paymentField = new DataSetField();
    paymentField.name = GlobalApplicationFields.TOTAL_FEES_DUE_NUM;
    paymentField.resultHeader = 'FeesDue';
    paymentField.dataType = ReportDataSetFieldDataType.Integer;
    paymentField.isStatic = true;
    paymentField.isAvailableColumn = false;
    fields.push(paymentField);

    const notesField = new DataSetField();
    notesField.name = GlobalApplicationFields.APPLICATION_NOTES_COUNT;
    notesField.resultHeader = 'Notes';
    notesField.dataType = ReportDataSetFieldDataType.Integer;
    notesField.isStatic = true;
    notesField.isAvailableColumn = false;
    fields.push(notesField);

    const assignedSignOffsField = new DataSetField();
    assignedSignOffsField.name = GlobalApplicationFields.ASSIGNED_SIGN_OFFS_COUNT;
    assignedSignOffsField.resultHeader = 'Assigned SignOffs';
    assignedSignOffsField.dataType = ReportDataSetFieldDataType.Integer;
    assignedSignOffsField.isStatic = true;
    assignedSignOffsField.isAvailableColumn = false;
    fields.push(assignedSignOffsField);

    const approvedSignOffsField = new DataSetField();
    approvedSignOffsField.name = GlobalApplicationFields.APPROVED_SIGN_OFFS_COUNT;
    approvedSignOffsField.resultHeader = 'Approved SignOffs';
    approvedSignOffsField.dataType = ReportDataSetFieldDataType.Integer;
    approvedSignOffsField.isStatic = true;
    approvedSignOffsField.isAvailableColumn = false;
    fields.push(approvedSignOffsField);

    const disapprovedSignOffsField = new DataSetField();
    disapprovedSignOffsField.name = GlobalApplicationFields.DISAPPROVED_SIGN_OFFS_COUNT;
    disapprovedSignOffsField.resultHeader = 'Disapproved SignOffs';
    disapprovedSignOffsField.dataType = ReportDataSetFieldDataType.Integer;
    disapprovedSignOffsField.isStatic = true;
    disapprovedSignOffsField.isAvailableColumn = false;
    fields.push(disapprovedSignOffsField);

    const inspectionsField = new DataSetField();
    inspectionsField.name = GlobalApplicationFields.INSPECTIONS_COUNT;
    inspectionsField.resultHeader = 'InspectionsCount';
    inspectionsField.dataType = ReportDataSetFieldDataType.Integer;
    inspectionsField.isStatic = true;
    inspectionsField.isAvailableColumn = false;
    fields.push(inspectionsField);

    const applicantSubmittedField = new DataSetField();
    applicantSubmittedField.name = GlobalApplicationFields.APPLICANT_SUBMITTED;
    applicantSubmittedField.resultHeader = 'ApplicantSubmitted';
    applicantSubmittedField.dataType = ReportDataSetFieldDataType.String;
    applicantSubmittedField.isStatic = true;
    applicantSubmittedField.isAvailableColumn = false;
    fields.push(applicantSubmittedField);

    const createDateField = new DataSetField();
    createDateField.name = GlobalApplicationFields.CREATE_DATE;
    createDateField.resultHeader = 'Create Date';
    createDateField.dataType = ReportDataSetFieldDataType.Date;
    createDateField.isStatic = true;
    createDateField.isAvailableColumn = false;
    fields.push(createDateField);

    const totalFeesNum = new DataSetField();
    totalFeesNum.name = GlobalApplicationFields.TOTAL_FEES_NUM;
    totalFeesNum.resultHeader = 'TotalFees';
    totalFeesNum.dataType = ReportDataSetFieldDataType.Decimal;
    totalFeesNum.isStatic = true;
    totalFeesNum.isAvailableColumn = false;
    fields.push(totalFeesNum);

    const totalFeesPaidNum = new DataSetField();
    totalFeesPaidNum.name = GlobalApplicationFields.TOTAL_FEES_PAID_NUM;
    totalFeesPaidNum.resultHeader = 'PaidFees';
    totalFeesPaidNum.dataType = ReportDataSetFieldDataType.Decimal;
    totalFeesPaidNum.isStatic = true;
    totalFeesPaidNum.isAvailableColumn = false;
    fields.push(totalFeesPaidNum);

    const assignedInspectionsCount = new DataSetField();
    assignedInspectionsCount.name = GlobalApplicationFields.ASSIGNED_INSPECTIONS_COUNT;
    assignedInspectionsCount.resultHeader = 'Assigned Inspections';
    assignedInspectionsCount.dataType = ReportDataSetFieldDataType.Integer;
    assignedInspectionsCount.isStatic = true;
    assignedInspectionsCount.isAvailableColumn = false;
    fields.push(assignedInspectionsCount);

    const approvedInspectionsCount = new DataSetField();
    approvedInspectionsCount.name = GlobalApplicationFields.APPROVED_INSPECTIONS_COUNT;
    approvedInspectionsCount.resultHeader = 'Approved Inspections';
    approvedInspectionsCount.dataType = ReportDataSetFieldDataType.Integer;
    approvedInspectionsCount.isStatic = true;
    approvedInspectionsCount.isAvailableColumn = false;
    fields.push(approvedInspectionsCount);

    const disapprovedInspectionsCount = new DataSetField();
    disapprovedInspectionsCount.name = GlobalApplicationFields.DISAPPROVED_INSPECTIONS_COUNT;
    disapprovedInspectionsCount.resultHeader = 'Disapproved Inspections';
    disapprovedInspectionsCount.dataType = ReportDataSetFieldDataType.Integer;
    disapprovedInspectionsCount.isStatic = true;
    disapprovedInspectionsCount.isAvailableColumn = false;
    fields.push(disapprovedInspectionsCount);

    return fields;
  }

  getInitialFieldsForDepartment(): DataSetField[] {
    if (
      this.currentEditingDept?.applicationGridDataSetId &&
      this.currentEditingDept.applicationGridDataSetId !== Utilities.EMPTY_GUID
    ) {
      return [];
    }

    const fields: DataSetField[] = [];

    const appNumberField = new DataSetField();
    appNumberField.name = GlobalApplicationFields.APPLICATION_NUMBER;
    appNumberField.resultHeader = 'App #';
    appNumberField.dataType = ReportDataSetFieldDataType.String;
    appNumberField.isStatic = false;
    // Added by Amisha on 05/22/2026 to keep the starter columns available by
    // default for new departments and visible in the downstream application grids.
    appNumberField.isAvailableColumn = true;
    fields.push(appNumberField);

    const appTypeField = new DataSetField();
    appTypeField.name = GlobalApplicationFields.WORKFLOW_NAME;
    appTypeField.resultHeader = 'App Type';
    appTypeField.dataType = ReportDataSetFieldDataType.String;
    appTypeField.isStatic = false;
    appTypeField.isAvailableColumn = true;
    fields.push(appTypeField);

    const appDateField = new DataSetField();
    appDateField.name = GlobalApplicationFields.APPLICATION_SUBMITTED_ON;
    appDateField.resultHeader = 'App Date';
    appDateField.dataType = ReportDataSetFieldDataType.Date;
    appDateField.isStatic = false;
    appDateField.isAvailableColumn = true;
    fields.push(appDateField);

    const issueDateField = new DataSetField();
    issueDateField.name = GlobalApplicationFields.ISSUED_ON;
    issueDateField.resultHeader = 'Issue Date';
    issueDateField.dataType = ReportDataSetFieldDataType.Date;
    issueDateField.isStatic = false;
    issueDateField.isAvailableColumn = true;
    fields.push(issueDateField);

    const siteAddressField = new DataSetField();
    siteAddressField.name = 'PARCEL_ADDRESS';
    siteAddressField.resultHeader = 'Site Address';
    siteAddressField.dataType = ReportDataSetFieldDataType.String;
    siteAddressField.isStatic = false;
    siteAddressField.isAvailableColumn = true;
    fields.push(siteAddressField);

    const applicantField = new DataSetField();
    applicantField.name = GlobalApplicationFields.SUBMITTER;
    applicantField.resultHeader = 'Applicant';
    applicantField.dataType = ReportDataSetFieldDataType.String;
    applicantField.isStatic = false;
    applicantField.isAvailableColumn = true;
    fields.push(applicantField);

    const ownerField = new DataSetField();
    ownerField.name = 'PARCEL_OWNER';
    ownerField.resultHeader = 'Owner';
    ownerField.dataType = ReportDataSetFieldDataType.String;
    ownerField.isStatic = false;
    ownerField.isAvailableColumn = true;
    fields.push(ownerField);

    const statusField = new DataSetField();
    statusField.name = GlobalApplicationFields.APPLICATION_STATUS;
    statusField.resultHeader = 'Status';
    statusField.dataType = ReportDataSetFieldDataType.String;
    statusField.isStatic = false;
    statusField.isAvailableColumn = true;
    fields.push(statusField);

    const currentActivityField = new DataSetField();
    currentActivityField.name = GlobalApplicationFields.CURRENT_ACTIVITY_JSON;
    currentActivityField.resultHeader = 'Current Activities';
    currentActivityField.dataType = ReportDataSetFieldDataType.String;
    currentActivityField.isStatic = false;
    currentActivityField.isAvailableColumn = true;
    fields.push(currentActivityField);

    return fields;
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Added by Amisha on 06/05/2026 to auto-publish the applications grid dataset
    // only for new departments and enable actions, while keeping a normal edit save
    // as save-only.
    const isNewDepartment = !this.isEditMode;
    this.isSaving = true;
    const val = this.form.value;
    let department: Department;

    if (this.isEditMode && this.editingId && this.currentEditingDept) {
      department = JSON.parse(JSON.stringify(this.currentEditingDept)) as Department;
    } else {
      department = new Department();
      department.clientId = this._context.client.id;
    }

    department.name = val.name;
    department.headerText = val.name;
    department.description = val.description;
    department.iconUrl = val.icon;
    department.isEnabled = val.enabled;
    department.requireDocumentReview = val.requireDocumentReview;

    const addr = new DepartmentAddress();
    if (this.currentEditingDept?.address?.id) {
        addr.id = this.currentEditingDept.address.id;
    }
    addr.name = "Default";
    addr.parentType = "Department";
    addr.address1 = val.address1 || '';
    addr.address2 = val.address2 || '';
    addr.city = val.city || '';
    addr.state = val.state || '';
    addr.zip = val.zip || '';
    department.address = addr;

    const phone = new DepartmentPhoneNumber();
    if (this.currentEditingDept?.phoneNumber?.id) {
        phone.id = this.currentEditingDept.phoneNumber.id;
    }
    phone.name = "Default";
    phone.parentType = "Department";
    phone.number = val.phone || '';
    department.phoneNumber = phone;

    const fax = new DepartmentPhoneNumber();
    if (this.currentEditingDept?.faxNumber?.id) {
        fax.id = this.currentEditingDept.faxNumber.id;
    }
    fax.name = "Default";
    fax.parentType = "Department";
    fax.number = val.fax || '';
    department.faxNumber = fax;


    this.persistDepartmentDataSet(department).subscribe({
      next: dataSetId => {
        if (dataSetId) {
          department.applicationGridDataSetId = dataSetId;
        }

        this._clientService.saveClientDepartment(department).subscribe({
          next: (savedDept) => {
            const finalizeWithOptionalDataSetPublish = (shouldPublishDataSet: boolean) => {
              if (shouldPublishDataSet) {
                this.runDepartmentDataSetSaveAndPublishAfterSave(savedDept);
              }

              this.finalizeSave(savedDept);
            };

            if (savedDept.isEnabled !== val.enabled) {
              const toggleCall = val.enabled
                ? this._clientService.enableClientDepartment(savedDept.id)
                : this._clientService.disableClientDepartment(savedDept.id);

              toggleCall.subscribe({
                next: (success) => {
                  if (success) savedDept.isEnabled = val.enabled;
                  finalizeWithOptionalDataSetPublish(isNewDepartment || !!val.enabled);
                },
                error: () => {
                  this._toastr.warning('Department saved, but failed to update enabled status.');
                  finalizeWithOptionalDataSetPublish(isNewDepartment);
                }
              });
            } else {
              finalizeWithOptionalDataSetPublish(isNewDepartment);
            }
          },
          error: (error) => {
            console.error('Failed to save department:', error);
            this._toastr.error('Failed to save department');
            this.isSaving = false;
          }
        });
      },
      error: error => {
        console.error('Failed to save dataset:', error);
        this._toastr.error('Failed to save applications grid dataset');
        this.isSaving = false;
      }
    });
  }

  private finalizeSave(savedDept: Department) {
    this._toastr.success(`Department ${this.isEditMode ? 'updated' : 'created'} successfully`);

    if (this.isEditMode) {
      const index = this.departments.findIndex(d => d.id === savedDept.id);
      if (index > -1) this.departments[index] = savedDept;
    } else {
      this.departments.push(savedDept);
    }

    this.isSaving = false;
    this.closeInlineEditor();
  }

  // Added by Amisha on 06/05/2026 to reuse the existing dataset save/publish APIs
  // after department create and enable so users do not have to open the dataset
  // designer and publish manually.
  private runDepartmentDataSetSaveAndPublishAfterSave(savedDept: Department) {
    if (!savedDept?.id) {
      return;
    }

    const saveAndPublish = (dataSet: DataSet) => {
      if (!dataSet) {
        return;
      }

      if (!dataSet.filterConfig) {
        dataSet.filterConfig = new FilterConfig({ fields: [] });
      }

      if (!dataSet.dataSetConfig) {
        dataSet.dataSetConfig = new WorkflowApplicationReportDataSet();
      }

      if (!dataSet.dataSetConfig.clientIds?.length) {
        dataSet.dataSetConfig.clientIds = [this._context.client.id];
      }

      dataSet.clientId = this._context.client.id;
      dataSet.name = savedDept.name?.trim()
        ? `${savedDept.name.trim()} Applications`
        : dataSet.name || `${savedDept.name} Applications`;

      // Added by Amisha on 06/05/2026 to prefer the dataset already linked on the
      // department during auto-create/enable publish, preventing a second detached
      // dataset from being created.
      if (
        !dataSet.id &&
        savedDept.applicationGridDataSetId &&
        savedDept.applicationGridDataSetId !== Utilities.EMPTY_GUID
      ) {
        dataSet.id = savedDept.applicationGridDataSetId;
      }

      dataSet.dataSetConfig = this.buildSerializedDataSetConfig(
        dataSet.dataSetConfig
      );

      const flattenedFilters: ItemSearchOptionFieldOption[] = [];
      this.getStaticFiltersForDepartment(savedDept.id).forEach(field => {
        (field.options || []).forEach(option => flattenedFilters.push(option));
      });
      dataSet.filterConfig.staticFilterInput = flattenedFilters;

      this._workflowService.saveDataSet(this.buildSerializedDataSetPayload(dataSet)).subscribe({
        next: resultDataSet => {
          const resultDataSetId = resultDataSet?.id;

          if (!resultDataSetId) {
            return;
          }

          const publishSavedDataSet = () => {
            this._workflowService.publishDataSet(resultDataSetId).subscribe({
              error: error => {
                console.error(
                  'Failed to publish applications grid dataset after department save:',
                  error
                );
              }
            });
          };

          if (savedDept.applicationGridDataSetId === resultDataSetId) {
            publishSavedDataSet();
            return;
          }

          const updatedDepartment = JSON.parse(
            JSON.stringify(savedDept)
          ) as Department;
          updatedDepartment.applicationGridDataSetId = resultDataSetId;

          // Added by Amisha on 06/05/2026 to keep the department link in sync when
          // saveDataSet returns a different id, so later edit/publish actions target
          // the same dataset that was auto-published here.
          this._clientService.saveClientDepartment(updatedDepartment).subscribe({
            next: syncedDepartment => {
              savedDept.applicationGridDataSetId = resultDataSetId;

              if (this.currentEditingDept?.id === syncedDepartment?.id) {
                this.currentEditingDept = syncedDepartment;
              }

              const departmentIndex = this.departments.findIndex(
                departmentItem => departmentItem.id === syncedDepartment?.id
              );

              if (departmentIndex > -1) {
                this.departments[departmentIndex] = syncedDepartment;
              }

              publishSavedDataSet();
            },
            error: error => {
              console.error(
                'Failed to sync applications grid dataset link after department save:',
                error
              );
            }
          });
        },
        error: error => {
          console.error(
            'Failed to save applications grid dataset after department save:',
            error
          );
        }
      });
    };

    if (this.currentEditingDept?.id === savedDept.id && this.ds && this.dataSetConfig) {
      saveAndPublish(this.ds);
      return;
    }

    if (
      savedDept.applicationGridDataSetId &&
      savedDept.applicationGridDataSetId !== Utilities.EMPTY_GUID
    ) {
      this._workflowService.getDataSet(savedDept.applicationGridDataSetId).subscribe({
        next: dataSet => {
          saveAndPublish(dataSet);
        },
        error: error => {
          console.error(
            'Failed to load applications grid dataset after department save:',
            error
          );
        }
      });
      return;
    }

    const defaultDataSet = new DataSet();
    defaultDataSet.clientId = this._context.client.id;
    defaultDataSet.filterConfig = new FilterConfig({ fields: [] });
    defaultDataSet.dataSetConfig = new WorkflowApplicationReportDataSet();
    defaultDataSet.dataSetConfig.clientIds = [this._context.client.id];
    defaultDataSet.dataSetConfig.fields = this.getStaticFieldsForDepartment();
    defaultDataSet.dataSetConfig.workflowIds = [];
    defaultDataSet.dataSetConfig.workflowFilters = [];
    defaultDataSet.dataSetConfig.fieldOrder = [
      new ReportDataSetOrder({
        fieldName: GlobalApplicationFields.APPLICATION_NUMBER,
        direction: SortDirection.Ascending,
        ordinal: 0
      })
    ];
    defaultDataSet.name = `${savedDept.name} Applications`;

    saveAndPublish(defaultDataSet);
  }

  toggleEnabled(dept: Department) {
    const action = dept.isEnabled ? 'disable' : 'enable';
    const serviceCall = dept.isEnabled
      ? this._clientService.disableClientDepartment(dept.id)
      : this._clientService.enableClientDepartment(dept.id);

    serviceCall.subscribe({
      next: (result) => {
        if (result) {
          const updatedDepartment = (result as Department)?.id
            ? (result as Department)
            : ({
                ...dept,
                isEnabled: !dept.isEnabled
              } as Department);

          dept.isEnabled = !dept.isEnabled;

          if (action === 'enable') {
            // Added by Amisha on 06/05/2026 to mirror the department create flow by
            // auto-saving and publishing the linked applications grid dataset on enable.
            this.runDepartmentDataSetSaveAndPublishAfterSave(updatedDepartment);
          }

          this._toastr.success(`Department "${dept.name}" has been ${action}d.`);
        } else {
          this._toastr.error(`Failed to ${action} department.`);
        }
      },
      error: (error) => {
        this._toastr.error(`Failed to ${action} department.`);
      }
    });
  }

  requestDelete(dept: Department) {
    this.pendingDeleteDept = dept;
    this.confirmData = {
      ...this.confirmData,
      open: true,
      modalDescp: `Are you sure you want to delete the department "${dept.name}"?`,
      modalSubDescp: 'This action cannot be undone.',
      yesNoBtn: true,
      replaceYesWith: 'DELETE',
      replaceNoWith: 'CANCEL',
    };
  }

  confirmDelete() {
    if (!this.pendingDeleteDept) { this.closeConfirm(); return; }
    const dept = this.pendingDeleteDept;
    this._clientService.deleteClientDepartment(dept.id).subscribe({
      next: (success) => {
        if (success) {
           this.departments = this.departments.filter(d => d.id !== dept.id);
           this._toastr.success(`Department "${dept.name}" deleted.`);
           
           if (this.currentEditingDept?.id === dept.id) {
               this.closeInlineEditor();
           }
        } else {
          this._toastr.error('Failed to delete department.');
        }
      },
      error: (error) => {
        this._toastr.error('Failed to delete department.');
      }
    });

    this.pendingDeleteDept = null;
    this.closeConfirm();
  }

  cancelDelete() {
    this.pendingDeleteDept = null;
    this.closeConfirm();
  }

  private closeConfirm() {
    this.confirmData = { ...this.confirmData, open: false };
  }

  onDatasetChange(data: any) {
    if (!this.dataSetConfig) {
      return;
    }

    this.dataSetConfig.fields = data.fields || [];
    this.dataSetConfig.workflowIds = data.workflowIds || [];
    this.dataSetConfig.workflowFilters = data.workflowFilters || [];
    this.refreshViewFromDataSet();
  }

  openColumnSettingsModal() {
    this.selectedColumnSettingsWorkflowId = null;
    this.selectedColumnSettingsField = null;
    this.columnSettingsTopOffsetPx = this.getColumnSettingsTopOffset();
    this.isColumnSettingsOpen = true;
    this.scrollToColumnSettings();
  }

  editColumn(column: any) {
    const field = (this.dataSetConfig?.fields || []).find(
      (existingField: DataSetField) => existingField.name === column?.name
    );

    this.selectedColumnSettingsWorkflowId = null;
    this.selectedColumnSettingsField = field ? { ...field } : null;
    this.columnSettingsTopOffsetPx = this.getColumnSettingsTopOffset();
    this.isColumnSettingsOpen = true;
    this.scrollToColumnSettings();
  }

  closeColumnSettingsModal() {
    this.isColumnSettingsOpen = false;
    this.selectedColumnSettingsWorkflowId = null;
    this.selectedColumnSettingsField = null;
    this.columnSettingsTopOffsetPx = 0;
  }

  handleColumnSettingsSave(field: any) {
    this.handleColumnSave(field);
    this.closeColumnSettingsModal();
  }

  // Added by Amisha on 04/24/2026: add report-style publish confirmation and draft/test actions to the department dataset editor while keeping department-specific APIs in this host component.
  onOpenPublishData() {
    this.publishDataSetModalData = {
      ...this.publishDataSetModalData,
      open: true
    };
  }

  onClosePublishData() {
    this.publishDataSetModalData = {
      ...this.publishDataSetModalData,
      open: false
    };
  }

  saveDepartmentDataSetOnly() {
    if (!this.canManageDepartmentDataSet()) {
      return;
    }

    this.isSavingDataSetDraft = true;
    const department = this.buildDepartmentDataSetContext();

    this.persistAndSyncDepartmentDataSet(department).subscribe({
      next: dataSetId => {
        this.isSavingDataSetDraft = false;
        this._toastr.success('Dataset saved successfully');
      },
      error: error => {
        console.error('Failed to save dataset:', error);
        this._toastr.error('Failed to save applications grid dataset');
        this.isSavingDataSetDraft = false;
      }
    });
  }

  testDepartmentDataSet() {
    if (!this.currentEditingDept?.id || !this.canManageDepartmentDataSet()) {
      this._toastr.warning('Please save the department dataset configuration first.');
      return;
    }

    this.isTestingDataSet = true;
    this.closeTestResults();
    const department = this.buildDepartmentDataSetContext();

    this.persistAndSyncDepartmentDataSet(department).subscribe({
      next: () => {
        this._workflowService.getDepartmentApplications(
          this.currentEditingDept.id,
          { pageNumber: 1, maxRows: 20 }
        ).subscribe({
          next: result => {
            const rowCount = result?.dataSetResult?.rows?.length || 0;
            this.resultColumns = this.createResultTableColumns(
              (result?.dataSet?.dataSetConfig?.fields || []).map(field => ({
                prop: (field.resultHeader || field.name || '').toLowerCase(),
                name: field.resultHeader || field.name
              }))
            );
            this.resultRows = (result?.dataSetResult?.rows || []).map(row => {
              const formattedRow = {
                id: row.id
              };

              Object.keys(row.values || {}).forEach(key => {
                formattedRow[key.toLowerCase()] = row.values[key];
              });

              return formattedRow;
            });
            this._toastr.success(`Retrieved ${rowCount} applications.`);
            this.isTestingDataSet = false;
          },
          error: error => {
            console.error('Failed to test dataset:', error);
            this._toastr.error('Failed to test applications grid dataset');
            this.isTestingDataSet = false;
          }
        });
      },
      error: error => {
        console.error('Failed to save dataset before test:', error);
        this._toastr.error('Failed to save applications grid dataset before testing');
        this.isTestingDataSet = false;
      }
    });
  }

  publishDepartmentDataSet() {
    if (!this.currentEditingDept?.id || !this.canManageDepartmentDataSet()) {
      this._toastr.warning('Please save the department dataset configuration first.');
      return;
    }

    this.onClosePublishData();
    this.isPublishingDataSet = true;
    const hadExistingLinkedDataSet =
      !!this.currentEditingDept?.applicationGridDataSetId &&
      this.currentEditingDept.applicationGridDataSetId !== Utilities.EMPTY_GUID;
    this.ds.name =
      this.buildDepartmentDataSetContext().name || this.ds.name || `${this.currentEditingDept.name} Applications`;
    this.ds.dataSetConfig = this.buildSerializedDataSetConfig();

    this._workflowService.saveDataSet(this.buildSerializedDataSetPayload()).subscribe({
      next: resultDataSet => {
        this.initializeLocalDataSet(resultDataSet);
        const dataSetId = resultDataSet?.id;

        if (!dataSetId) {
          this._toastr.error('Unable to publish dataset');
          this.isPublishingDataSet = false;
          return;
        }

        this._workflowService.publishDataSet(dataSetId).subscribe({
          next: () => {
            this._toastr.success('Dataset published successfully');
            this.isPublishingDataSet = false;
          },
          error: error => {
            console.error('Failed to publish dataset:', error);
            this._toastr.error('Failed to publish applications grid dataset');
            this.isPublishingDataSet = false;
          }
        });
      },
      error: error => {
        console.error('Failed to save dataset before publish:', error);
        this._toastr.error('Failed to save applications grid dataset before publishing');
        this.isPublishingDataSet = false;
      }
    });
  }

  private scrollToColumnSettings() {
    // Added by Amisha on 04/24/2026: scroll the Settings tab container to the column settings modal so users do not have to manually hunt for the overlay after opening it.
    const scrollModalIntoView = () => {
      const modal = document.querySelector(
        'wm-column-settings .wm-overlay-content'
      ) as HTMLElement | null;
      const scrollContainer = document.querySelector(
        '.tab-content'
      ) as HTMLElement | null;

      if (!modal || !scrollContainer) {
        return false;
      }

      const rect = modal.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const desiredTop = 88;
      const targetScrollTop =
        scrollContainer.scrollTop + (rect.top - containerRect.top) - desiredTop;
      const nextTop = Math.max(targetScrollTop, 0);

      scrollContainer.scrollTop = nextTop;
      scrollContainer.scrollTo?.({
        top: nextTop,
        behavior: 'smooth'
      });

      modal.setAttribute('tabindex', '-1');
      modal.focus({ preventScroll: true });

      return true;
    };

    let attempts = 0;
    const tryScroll = () => {
      attempts++;
      const didScroll = scrollModalIntoView();

      if (!didScroll && attempts < 6) {
        requestAnimationFrame(tryScroll);
      }
    };

    requestAnimationFrame(tryScroll);
  }

  private getColumnSettingsTopOffset(): number {
    const cardElement = this.datasetDesignerCard?.nativeElement;
    const tableAnchorElement = this.commonDynamicTableAnchor?.nativeElement;

    if (!cardElement || !tableAnchorElement) {
      return 0;
    }

    const cardRect = cardElement.getBoundingClientRect();
    const tableRect = tableAnchorElement.getBoundingClientRect();
    const desiredGapAboveTable = 12;
    const baseContainedPadding = 16;
    const targetPaddingTop =
      Math.max(tableRect.top - cardRect.top - desiredGapAboveTable, baseContainedPadding);

    return Math.max(targetPaddingTop - baseContainedPadding, 0);
  }

  addWorkflow() {
    this.ensureDataSetConfigDefaults();
    this.workflowRowKeys.push(this.createRowKey());
    this.dataSetConfig.workflowIds.push('');
    this.dataSetConfig.workflowFilters.push(this.createWorkflowFilterConfig());
    this.refreshViewFromDataSet();
  }

  deleteWorkflow(index: number) {
    const workflowId = this.dataSetConfig?.workflowIds?.[index];

    this.workflowRowKeys.splice(index, 1);
    this.dataSetConfig.workflowIds.splice(index, 1);
    this.dataSetConfig.workflowFilters.splice(index, 1);

    if (!this.hasWorkflowOccurrence(workflowId)) {
      this.clearWorkflowArtifacts(workflowId);
    }

    this.refreshViewFromDataSet();
  }

  selectWorkflow(index: number, workflow: Workflow) {
    if (!this.dataSetConfig) {
      return;
    }

    const workflowId = workflow.id;
    const currentRow = this.rows[index];
    const copySourceRow = this.getCopySourceRow(workflowId, currentRow?.rowKey);
    const copiedFilterParams =
      copySourceRow?.filterParams || this.workflowFilterParamsCache[workflowId] || null;
    const copiedFilters = this.getCachedRowFilters(copySourceRow);
    const copiedFilterConfig = this.cloneWorkflowFilterConfig(
      copySourceRow?.workflowFilterConfig?.filtersList?.length
        ? copySourceRow.workflowFilterConfig
        : this.createWorkflowFilterConfigFromFilters(
            copiedFilters.length ? copiedFilters : copySourceRow?.filters || [],
            workflowId
          ),
      workflowId
    );
    const previousWorkflowId =
      currentRow?.workflowId || this.dataSetConfig.workflowIds[index];

    if (copiedFilterParams) {
      this.workflowFilterParamsCache[workflowId] = copiedFilterParams;
    }

    this.dataSetConfig.workflowIds[index] = workflowId;
    this.setWorkflowFilterConfigAtIndex(index, copiedFilterConfig);

    if (
      previousWorkflowId &&
      previousWorkflowId !== workflowId &&
      !this.hasWorkflowOccurrence(previousWorkflowId, index)
    ) {
      this.clearWorkflowArtifacts(previousWorkflowId);
    }

    this.refreshViewFromDataSet();
    const selectedRow = this.rows[index];

    if (selectedRow?.workflowId && !selectedRow.filterParams) {
      this.loadRowFilterData(selectedRow);
    }
  }

  openCellDropdown(row: WorkflowRow, col: Column) {
    if (!row.workflowId) {
      this.cellDropdownEntities = [];
      return;
    }

    this._workflowService.getWorkflowEntities(row.workflowId).subscribe(entities => {
      this.cellDropdownEntities = entities || [];
    });
  }

  selectCellEntity(row: WorkflowRow, col: Column, entity: any) {
    const templateCode = entity?.templateCode || entity?.name;
    const field = this.getField(col.name);

    if (!templateCode || !row.workflowId || !field) {
      return;
    }

    if (!Array.isArray(field.mappings)) {
      field.mappings = [];
    }

    field.mappings.push({
      workflowId: row.workflowId,
      columnIndex:
        this.dataSetConfig.fields.findIndex(
          (existingField: DataSetField) => existingField.name === field.name
        ) + 1,
      templateCode
    });

    this.refreshViewFromDataSet();
  }

  deleteCellValue(row: WorkflowRow, col: Column, index: number) {
    const field = this.getField(col.name);

    if (!field || !row.workflowId) {
      return;
    }

    const mappingsForWorkflow = (field.mappings || []).filter(
      mapping => mapping.workflowId === row.workflowId
    );
    const mappingToRemove = mappingsForWorkflow[index];

    if (!mappingToRemove) {
      return;
    }

    field.mappings = (field.mappings || []).filter(
      mapping => mapping !== mappingToRemove
    );

    this.refreshViewFromDataSet();
  }

  moveColumnLeft(column: Column) {
    this.moveColumn(column, -1);
  }

  moveColumnRight(column: Column) {
    this.moveColumn(column, 1);
  }

  deleteColumn(column: Column) {
    if (!this.dataSetConfig) {
      return;
    }

    // Added by Amisha on 05/21/2026 to keep Janus delete behavior aligned with
    // the legacy dataset designer, which never removes protected/system fields.
    const field = this.getField(column?.name);

    if (field?.isStatic) {
      return;
    }

    this.dataSetConfig.fields = (this.dataSetConfig.fields || []).filter(
      (field: DataSetField) => field.name !== column?.name
    );
    this.refreshViewFromDataSet();
  }

  reorderColumn(event: {
    column: Column;
    targetColumn: Column;
    previousIndex: number;
    currentIndex: number;
  }) {
    if (!this.dataSetConfig?.fields || !event?.column || !event?.targetColumn) {
      return;
    }

    const fields = this.dataSetConfig.fields;
    const sourceIndex = fields.findIndex(field => field.name === event.column.name);
    const targetIndex = fields.findIndex(field => field.name === event.targetColumn.name);

    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      return;
    }

    const [field] = fields.splice(sourceIndex, 1);
    fields.splice(targetIndex, 0, field);
    this.refreshViewFromDataSet();
  }

  onRowFiltersChange2(e: any, row: WorkflowRow) {
    if (!row?.workflowId) {
      return;
    }

    const rowIndex = this.rows.findIndex(r => r.rowKey === row.rowKey);

    if (rowIndex < 0) {
      return;
    }

    const workflowFilterConfig = this.getWorkflowFilterConfigAtIndex(rowIndex);

    workflowFilterConfig.workflowId = row.workflowId;
    workflowFilterConfig.filtersList = this.buildWorkflowFilterList(e);
    row.workflowFilterConfig = this.cloneWorkflowFilterConfig(
      workflowFilterConfig,
      row.workflowId
    );
    row.filters = this.buildFiltersFromWorkflowConfig(
      workflowFilterConfig,
      row.filterParams || []
    );
    this.setWorkflowFilterConfigAtIndex(rowIndex, workflowFilterConfig);
  }

  handleColumnSave(field: any) {
    this.ensureDataSetConfigDefaults();
    // Added by Amisha on 05/21/2026 so saved columns preserve the same
    // protected-column classification after Janus rehydrates the dataset.
    const staticFieldDefinition = this.getStaticFieldDefinition(field?.name);

    const existingColumnIndex = this.dataSetConfig.fields.findIndex(
      (existingField: DataSetField) =>
        existingField.id === field.id || existingField.name === field.name
    );

    if (existingColumnIndex > -1) {
      this.dataSetConfig.fields[existingColumnIndex] = {
        ...this.dataSetConfig.fields[existingColumnIndex],
        ...field,
        mappings: this.dataSetConfig.fields[existingColumnIndex].mappings || [],
        isStatic:
          this.dataSetConfig.fields[existingColumnIndex].isStatic ??
          staticFieldDefinition?.isStatic ??
          false
      };
    } else {
      this.dataSetConfig.fields.push({
        ...field,
        mappings: field.mappings || [],
        isStatic: staticFieldDefinition?.isStatic ?? false
      });
    }

    this.refreshViewFromDataSet();
  }

  getWorkflowDisplayName(
    workflowId?: string | null,
    fallback = 'Select Workflow'
  ): string {
    if (!workflowId) {
      return fallback;
    }

    if (workflowId === this._context.previousSystemDataWorkflowId) {
      return 'Previous System Data';
    }

    if (workflowId === Utilities.EMPTY_GUID) {
      return 'All Workflows';
    }

    const normalizedWorkflowId = workflowId.toLowerCase();
    const workflow = (this.availableWorkflows || []).find((w: any) => {
      const candidateIds = [
        w?.id,
        w?.versionId,
        w?.workflowId,
        w?.version?.id,
        w?.version?.workflowId
      ]
        .filter(Boolean)
        .map((value: string) => value.toLowerCase());

      return candidateIds.includes(normalizedWorkflowId);
    });

    return workflow?.name || workflow?.version?.name || workflowId || fallback;
  }

  private moveColumn(column: Column, direction: -1 | 1) {
    if (!this.dataSetConfig?.fields) {
      return;
    }

    const fields = this.dataSetConfig.fields;
    const currentIndex = fields.findIndex(field => field.name === column?.name);
    const targetIndex = currentIndex + direction;

    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= fields.length
    ) {
      return;
    }

    const [field] = fields.splice(currentIndex, 1);
    fields.splice(targetIndex, 0, field);
    this.refreshViewFromDataSet();
  }

  private initializeDepartmentDataSet() {
    if (!this.currentEditingDept) {
      return;
    }

    this.isDatasetLoading = true;

    this.getWorkflowsForDepartment().subscribe({
      next: (workflows) => {
        this.availableWorkflows = workflows || [];

        const dataSetId = this.currentEditingDept?.applicationGridDataSetId;

        if (!dataSetId || dataSetId === Utilities.EMPTY_GUID) {
          const ds = new DataSet();
          ds.clientId = this._context.client.id;
          ds.filterConfig = new FilterConfig({ fields: [] });
          ds.dataSetConfig = new WorkflowApplicationReportDataSet();
          ds.dataSetConfig.clientIds = [this._context.client.id];
          ds.dataSetConfig.fields = this.getStaticFieldsForDepartment();
          ds.dataSetConfig.workflowIds = [];
          ds.dataSetConfig.workflowFilters = [];
          ds.dataSetConfig.fieldOrder = [
            new ReportDataSetOrder({
              fieldName: GlobalApplicationFields.APPLICATION_NUMBER,
              direction: SortDirection.Ascending,
              ordinal: 0
            })
          ];
          ds.name = `${this.currentEditingDept.name} Applications`;
          this.initializeLocalDataSet(ds);
          this.isDatasetLoading = false;
          return;
        }

        this._workflowService.getDataSet(dataSetId).subscribe({
          next: ds => {
            this.initializeLocalDataSet(ds);
            this.initializeAvailableColumns();
            this.updateAvailableFilterFields();
            this.isDatasetLoading = false;
          },
          error: error => {
            console.error('Failed to load department dataset:', error);
            this._toastr.error('Failed to load applications grid dataset');
            this.isDatasetLoading = false;
          }
        });
      },
      error: (error) => {
        console.error('Failed to load workflows for department dataset:', error);
        this.isDatasetLoading = false;
      }
    });
  }

  private initializeLocalDataSet(ds: DataSet) {
    this.ds = ds;

    if (!this.ds.filterConfig) {
      this.ds.filterConfig = new FilterConfig({ fields: [] });
    }

    if (!this.ds.dataSetConfig) {
      this.ds.dataSetConfig = new WorkflowApplicationReportDataSet();
    }

    if (!this.ds.dataSetConfig.clientIds?.length) {
      this.ds.dataSetConfig.clientIds = [this._context.client.id];
    }

    this.dataSetConfig = this.ds.dataSetConfig;
    this.ensureDataSetConfigDefaults();
    // Added by Amisha on 05/21/2026 to restore protected-column metadata from
    // department defaults before rebuilding the shared dynamic-table view.
    this.applyStaticFieldMetadata();
    this.mergeStaticFieldsIntoDataSet();

    if (!this.ds.name && this.currentEditingDept?.name) {
      this.ds.name = `${this.currentEditingDept.name} Applications`;
    }

    this.refreshViewFromDataSet();
  }

  private mergeStaticFieldsIntoDataSet() {
    // Added by Amisha on 05/21/2026 to merge only true protected/system fields
    // so starter columns like Application Number stay editable like legacy.
    const staticFields = this.getProtectedStaticFieldsForDepartment();

    staticFields.forEach(staticField => {
      const existingField = (this.dataSetConfig?.fields || []).find(
        field => field.name === staticField.name
      );

      if (!existingField) {
        this.dataSetConfig.fields.push(staticField);
        return;
      }

      existingField.isStatic = true;
    });
  }

  private ensureDataSetConfigDefaults() {
    if (!this.dataSetConfig) {
      return;
    }

    if (!Array.isArray(this.dataSetConfig.fields)) {
      this.dataSetConfig.fields = [];
    }

    if (!Array.isArray(this.dataSetConfig.workflowIds)) {
      this.dataSetConfig.workflowIds = [];
    }

    if (!Array.isArray(this.dataSetConfig.workflowFilters)) {
      this.dataSetConfig.workflowFilters = [];
    }

    if (!Array.isArray(this.dataSetConfig.fieldOrder)) {
      this.dataSetConfig.fieldOrder = [];
    }

    this.ensureWorkflowFiltersAligned();
  }

  private refreshViewFromDataSet() {
    if (!this.dataSetConfig) {
      return;
    }

    const fields = this.dataSetConfig.fields || [];
    const workflowIds = this.dataSetConfig.workflowIds || [];
    const previousRowsByRowKey: { [rowKey: string]: WorkflowRow } = {};

    this.syncWorkflowRowKeys();
    this.pruneWorkflowState(workflowIds);

    this.rows.forEach(row => {
      if (row?.rowKey) {
        previousRowsByRowKey[row.rowKey] = row;
      }
    });

    this.columns = fields.map((field: DataSetField) => ({
      id: field.id,
      name: field.name,
      resultHeader: field.resultHeader,
      dataType: field.dataType,
      mappings: field.mappings || [],
      outputFormula: field.outputFormula || null,
      conditionalOutput: field.conditionalOutput || false,
      // Added by Amisha on 05/21/2026 so the shared table can decide whether
      // to show delete without needing direct access to dataset field objects.
      isStatic: !!field.isStatic
    }));

    this.rows = workflowIds.map((workflowId: string, index: number) => {
      const rowKey = this.workflowRowKeys[index];
      const previousRow = previousRowsByRowKey[rowKey];
      const workflowFilterConfig = this.cloneWorkflowFilterConfig(
        this.getWorkflowFilterConfigAtIndex(index),
        workflowId
      );
      const row: WorkflowRow =
        previousRow || {
          rowKey,
          workflow: '',
          workflowId
        };
      const workflowChanged = row.workflowId !== workflowId;

      row.rowKey = rowKey;
      row.workflowId = workflowId;
      row.workflowFilterConfig = workflowFilterConfig;
      row.filterBuilderId =
        !row.filterBuilderId || workflowChanged
          ? this.createFilterBuilderId(row.rowKey, workflowId)
          : row.filterBuilderId;

      fields.forEach((field: DataSetField) => {
        row[field.name] = (field.mappings || [])
          .filter(mapping => mapping.workflowId === workflowId)
          .map(mapping => mapping.templateCode);
      });

      if (!workflowId) {
        row.filterParams = null;
        row.filters = [];
        return row;
      }

      const cachedFilterParams =
        this.workflowFilterParamsCache[workflowId] || previousRow?.filterParams || null;
      const cachedRowFilters = this.getCachedRowFilters(previousRow);

      row.filterParams = cachedFilterParams;
      row.filters =
        cachedRowFilters.length && !workflowChanged
          ? cachedRowFilters
          : this.buildFiltersFromWorkflowConfig(
              workflowFilterConfig,
              cachedFilterParams || []
            );

      return row;
    });

    this.attachWorkflowNames();
    this.rows.forEach(row => {
      if (row.workflowId && !row.filterParams) {
        this.loadRowFilterData(row);
      }
    });

    this.initializeAvailableColumns();
    this.updateAvailableFilterFields();
  }

  private syncWorkflowRowKeys() {
    const workflowIds = this.dataSetConfig?.workflowIds || [];

    while (this.workflowRowKeys.length < workflowIds.length) {
      this.workflowRowKeys.push(this.createRowKey());
    }

    if (this.workflowRowKeys.length > workflowIds.length) {
      this.workflowRowKeys = this.workflowRowKeys.slice(0, workflowIds.length);
    }
  }

  private createRowKey(): string {
    return `workflow_row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private createWorkflowFilterConfig(workflowId?: string | null): WorkflowFilter {
    const workflowFilter = new WorkflowFilter();
    workflowFilter.workflowId = workflowId || '';
    workflowFilter.filtersList = [];
    return workflowFilter;
  }

  private cloneWorkflowFilterConfig(
    workflowFilter: WorkflowFilter | null | undefined,
    workflowId?: string | null
  ): WorkflowFilter {
    const clonedWorkflowFilter = new WorkflowFilter();
    clonedWorkflowFilter.workflowId = workflowId || workflowFilter?.workflowId || '';
    clonedWorkflowFilter.filtersList = (workflowFilter?.filtersList || []).map(
      filter => new ItemSearchOptionFieldOption({ ...filter })
    );
    return clonedWorkflowFilter;
  }

  private ensureWorkflowFiltersAligned() {
    const workflowIds = this.dataSetConfig?.workflowIds || [];
    const existingWorkflowFilters = this.dataSetConfig?.workflowFilters || [];
    const workflowFilterQueue: { [workflowId: string]: WorkflowFilter[] } = {};

    existingWorkflowFilters.forEach(filter => {
      const workflowId = filter?.workflowId || '';

      if (!workflowFilterQueue[workflowId]) {
        workflowFilterQueue[workflowId] = [];
      }

      if (!Array.isArray(filter.filtersList)) {
        filter.filtersList = [];
      }

      workflowFilterQueue[workflowId].push(filter);
    });

    this.dataSetConfig.workflowFilters = workflowIds.map(workflowId => {
      const nextFilter = (workflowFilterQueue[workflowId || ''] || []).shift();

      if (nextFilter) {
        nextFilter.workflowId = workflowId || '';
        return nextFilter;
      }

      return this.createWorkflowFilterConfig(workflowId);
    });
  }

  private getWorkflowFilterConfigAtIndex(index: number): WorkflowFilter {
    const workflowId = this.dataSetConfig?.workflowIds?.[index] || '';

    if (!Array.isArray(this.dataSetConfig.workflowFilters)) {
      this.dataSetConfig.workflowFilters = [];
    }

    while (this.dataSetConfig.workflowFilters.length <= index) {
      const nextWorkflowId =
        this.dataSetConfig.workflowIds[this.dataSetConfig.workflowFilters.length] || '';
      this.dataSetConfig.workflowFilters.push(
        this.createWorkflowFilterConfig(nextWorkflowId)
      );
    }

    const workflowFilter =
      this.dataSetConfig.workflowFilters[index] ||
      this.createWorkflowFilterConfig(workflowId);

    workflowFilter.workflowId = workflowId;

    if (!Array.isArray(workflowFilter.filtersList)) {
      workflowFilter.filtersList = [];
    }

    this.dataSetConfig.workflowFilters[index] = workflowFilter;
    return workflowFilter;
  }

  private setWorkflowFilterConfigAtIndex(
    index: number,
    workflowFilter: WorkflowFilter | null | undefined
  ) {
    const workflowId = this.dataSetConfig?.workflowIds?.[index] || '';

    if (!Array.isArray(this.dataSetConfig.workflowFilters)) {
      this.dataSetConfig.workflowFilters = [];
    }

    while (this.dataSetConfig.workflowFilters.length <= index) {
      this.dataSetConfig.workflowFilters.push(this.createWorkflowFilterConfig());
    }

    this.dataSetConfig.workflowFilters[index] = this.cloneWorkflowFilterConfig(
      workflowFilter,
      workflowId
    );
  }

  private buildFiltersFromWorkflowConfig(
    workflowFilter: WorkflowFilter | null | undefined,
    filterParams: FilterBuilderParam[]
  ): FilterBuilderFilter[] {
    if (!workflowFilter?.filtersList?.length || !filterParams?.length) {
      return [];
    }

    return workflowFilter.filtersList
      .map(filterItem => {
        let value = null;
        let valueString = null;

        if (filterItem.searchText && filterItem.searchText.indexOf('{') > -1) {
          value = JSON.parse(filterItem.searchText);
        } else if (filterItem.strOperator == 'in' && filterItem.searchText) {
          value = filterItem.searchText.split(',');
        } else {
          valueString = filterItem.searchText;
        }

        const filterParam = filterParams.find(
          param => param.name === filterItem.title
        );

        if (!filterParam) {
          return null;
        }

        return new FilterBuilderFilter({
          param: filterParam,
          value: valueString,
          queryType: filterItem.strOperator,
          customValue: value,
          inputType: filterItem.inputType,
          dataEntityTypeCode: filterItem.dataEntityTypeCode,
          paramId: filterParam.id
        });
      })
      .filter(filter => filter !== null);
  }

  private buildWorkflowFilterList(filtersOutput: any): ItemSearchOptionFieldOption[] {
    const filterKeys = Object.keys(filtersOutput || {});

    return filterKeys.flatMap(key =>
      (filtersOutput[key] || []).map(
        filter =>
          new ItemSearchOptionFieldOption({
            strOperator: filter.type,
            title: filter.id,
            filterText: filter.value,
            searchText: filter.value,
            inputType: filter.inputType,
            dataEntityTypeCode: filter.dataEntityTypeCode
          })
      )
    );
  }

  private createWorkflowFilterConfigFromFilters(
    filters: FilterBuilderFilter[] | null | undefined,
    workflowId: string
  ): WorkflowFilter {
    const workflowFilter = this.createWorkflowFilterConfig(workflowId);

    workflowFilter.filtersList = (filters || []).map(filter => {
      let value = filter.value;

      if (
        filter.queryType === 'range' &&
        (filter.value === 'custom' ||
          filter.param?.inputType === 'text' ||
          filter.param?.inputType === 'number') &&
        filter.customValue &&
        filter.customValue.start &&
        filter.customValue.end
      ) {
        value = JSON.stringify(filter.customValue);
      } else if (filter.queryType === 'in' && Array.isArray(filter.customValue)) {
        value = filter.customValue.join(',');
      }

      return new ItemSearchOptionFieldOption({
        strOperator: filter.queryType,
        title: filter.paramId || filter.param?.id,
        filterText: value,
        searchText: value,
        inputType: filter.inputType || filter.param?.inputType,
        dataEntityTypeCode: filter.dataEntityTypeCode
      });
    });

    return workflowFilter;
  }

  private getCachedRowFilters(row: WorkflowRow | null | undefined): FilterBuilderFilter[] {
    if (!row?.filterBuilderId || !row?.filterParams?.length) {
      return [];
    }

    try {
      const stringData = localStorage.getItem(`filter-builder-${row.filterBuilderId}`);

      if (!stringData) {
        return [];
      }

      return (JSON.parse(stringData) || [])
        .map((filter: FilterBuilderFilterFlat) => {
          const param = (row.filterParams || []).find(
            filterParam => filterParam.id === filter.paramId
          );

          if (!param) {
            return null;
          }

          return FilterBuilderFilter.unflatten({
            ...filter,
            ...param
          });
        })
        .filter(filter => !!filter);
    } catch {
      return [];
    }
  }

  private getCopySourceRow(workflowId: string, excludedRowKey?: string): WorkflowRow | null {
    return (
      this.rows.find(
        row => row.rowKey !== excludedRowKey && row.workflowId === workflowId
      ) || null
    );
  }

  private hasWorkflowOccurrence(
    workflowId: string | null | undefined,
    excludedIndex?: number
  ): boolean {
    if (!workflowId) {
      return false;
    }

    return (this.dataSetConfig.workflowIds || []).some(
      (id, index) => index !== excludedIndex && id === workflowId
    );
  }

  private attachWorkflowNames() {
    this.rows.forEach(row => {
      row.workflow = this.getWorkflowDisplayName(row.workflowId, '');
    });
  }

  private getField(columnName: string): DataSetField | undefined {
    return (this.dataSetConfig.fields || []).find(
      (field: DataSetField) => field.name === columnName
    );
  }

  private clearWorkflowArtifacts(workflowId?: string | null) {
    if (!workflowId) {
      return;
    }

    delete this.workflowFilterParamsCache[workflowId];

    (this.dataSetConfig.fields || []).forEach((field: DataSetField) => {
      field.mappings = (field.mappings || []).filter(
        mapping => mapping.workflowId !== workflowId
      );
    });
  }

  private pruneWorkflowState(workflowIds: string[]) {
    const validWorkflowIds = new Set((workflowIds || []).filter(Boolean));

    Object.keys(this.workflowFilterParamsCache).forEach(workflowId => {
      if (!validWorkflowIds.has(workflowId)) {
        delete this.workflowFilterParamsCache[workflowId];
      }
    });
  }

  private createFilterBuilderId(rowKey: string, workflowId?: string | null): string {
    const workflowKey = workflowId || 'workflow';
    return `departmentDataSetFilters_${workflowKey}_${rowKey}`;
  }

  private loadRowFilterData(row: WorkflowRow) {
    if (!row?.workflowId) {
      row.filterParams = null;
      row.filters = [];
      return;
    }

    const rowIndex = this.rows.findIndex(r => r.rowKey === row.rowKey);
    const workflowFilterConfig =
      rowIndex > -1
        ? this.getWorkflowFilterConfigAtIndex(rowIndex)
        : this.createWorkflowFilterConfig(row.workflowId);
    const cachedFilterParams = this.workflowFilterParamsCache[row.workflowId];

    if (cachedFilterParams) {
      row.filterParams = cachedFilterParams;
      row.workflowFilterConfig = this.cloneWorkflowFilterConfig(
        workflowFilterConfig,
        row.workflowId
      );
      row.filters = this.buildFiltersFromWorkflowConfig(
        workflowFilterConfig,
        cachedFilterParams
      );
      return;
    }

    this.filterWorkflow(row.workflowId, workflowFilterConfig).then(result => {
      const currentRow = this.rows.find(r => r.rowKey === row.rowKey);

      if (!currentRow || currentRow.workflowId !== row.workflowId) {
        return;
      }

      this.workflowFilterParamsCache[row.workflowId] = result.filterParams;
      currentRow.workflowFilterConfig = this.cloneWorkflowFilterConfig(
        workflowFilterConfig,
        currentRow.workflowId
      );
      currentRow.filterParams = result.filterParams;
      currentRow.filters = result.filters;
    });
  }

  private async filterWorkflow(
    workflowId: string,
    workflowFilterConfig?: WorkflowFilter | null
  ): Promise<{
    filterParams: FilterBuilderParam[];
    filters: FilterBuilderFilter[];
  }> {
    const filter = this.cloneWorkflowFilterConfig(workflowFilterConfig, workflowId);
    let entityObs = null;

    if (workflowId !== this._context.previousSystemDataWorkflowId) {
      if (workflowId !== Utilities.EMPTY_GUID) {
        entityObs = this._workflowService.getDraftWorkflowEntities(workflowId);
      } else {
        entityObs = this._workflowService.getDataEntities(null, null, true);
      }
    } else {
      const fields = await this._workflowService.getPreviousSystemDataFields(
        this._context.client.id
      );

      entityObs = of(
        fields.map(e => {
          if (e.dataType == ReportDataSetFieldDataType.Date) {
            return new DateDataEntity({
              templateCode: e.name,
              label: e.name
            });
          }

          return new FreeFormTextDataEntity({
            templateCode: e.name,
            label: e.name
          });
        })
      );
    }

    return await new Promise(resolve => {
      entityObs.subscribe(entities => {
        const filterParams: FilterBuilderParam[] = entities.map(e => ({
          name: e.templateCode,
          id: e.templateCode,
          paramId: e.templateCode,
          inputType: DataSet.resolveInputType(e),
          dataEntityTypeCode: e.Type,
          types: DataSet.resolveTypes(e),
          options: (() => {
            const os = DataSet.resolveOptions(e);

            if (os) {
              return os.map(o => ({ name: o, value: o }));
            }

            return null;
          })()
        }));

        const filters = this.buildFiltersFromWorkflowConfig(filter, filterParams);

        resolve({
          filterParams,
          filters: filters.filter(f => f)
        });
      });
    });
  }

  private persistDepartmentDataSet(department: Department): Observable<string | null> {
    if (!this.isEditMode || !this.ds || !this.dataSetConfig) {
      return of(department.applicationGridDataSetId || null);
    }

    if (!this.ds.filterConfig) {
      this.ds.filterConfig = new FilterConfig({ fields: [] });
    }

    this.ds.clientId = this._context.client.id;
    this.ds.name = `${department.name} Applications`;
    this.ds.dataSetConfig = this.buildSerializedDataSetConfig();

    const flattenedFilters: ItemSearchOptionFieldOption[] = [];
    this.getStaticFiltersForDepartment().forEach(field => {
      (field.options || []).forEach(option => flattenedFilters.push(option));
    });
    this.ds.filterConfig.staticFilterInput = flattenedFilters;

    return new Observable<string | null>(observer => {
      this._workflowService.saveDataSet(this.buildSerializedDataSetPayload()).subscribe({
        next: savedDataSet => {
          // Added by Amisha on 05/21/2026 to run the same rehydration path after
          // save that initial load uses, preventing Janus-only column drift.
          this.initializeLocalDataSet(savedDataSet);
          observer.next(savedDataSet?.id || null);
          observer.complete();
        },
        error: error => observer.error(error)
      });
    });
  }

  private buildSerializedDataSetConfig(
    dataSetConfig: WorkflowApplicationReportDataSet = this.dataSetConfig
  ): any {
    return {
      ...dataSetConfig,
      fields: (dataSetConfig?.fields || []).map(field => {
        // Added by Amisha on 05/21/2026 to keep the Janus department dataset
        // save path aligned with Janus reports now that column availability is editable.
        const { isStatic, ...rest } = field as any;
        return {
          ...rest,
          mappings: field.mappings || []
        };
      }),
      fieldOrder: (dataSetConfig?.fieldOrder || []).map((fieldOrder, index) => ({
        ...fieldOrder,
        ordinal: index,
        direction:
          Number(fieldOrder.direction) === SortDirection.Descending
            ? String(SortDirection.Descending)
            : String(SortDirection.Ascending)
      })),
      workflowFilters: (dataSetConfig?.workflowFilters || [])
        .map(filter => ({
          ...filter,
          filtersList: (filter.filtersList || [])
            .filter(filterItem => {
              const hasValue =
                (filterItem?.filterText || '') !== '' ||
                (filterItem?.searchText || '') !== '' ||
                (filterItem?.strOperator || '') === 'exists' ||
                (filterItem?.strOperator || '') === 'not exists';

              return hasValue;
            })
            .map(filterItem => ({
              ...filterItem
            }))
        }))
        .filter(filter => (filter.filtersList || []).length > 0)
    };
  }

  private buildSerializedDataSetPayload(dataSet: DataSet = this.ds): any {
    return {
      ...dataSet,
      dataSetConfig: this.buildSerializedDataSetConfig(
        dataSet?.dataSetConfig || this.dataSetConfig
      )
    };
  }

  private applyStaticFieldMetadata() {
    // Added by Amisha on 05/21/2026 to reclassify only department-level
    // protected fields after API loads, leaving starter columns editable.
    const staticFieldNames = new Set(
      this.getProtectedStaticFieldsForDepartment().map(field => field.name)
    );

    (this.dataSetConfig?.fields || []).forEach(field => {
      field.isStatic = staticFieldNames.has(field.name);
    });
  }

  private buildDepartmentDataSetContext(): Department {
    // Added by Amisha on 04/24/2026: keep the department name and dataset name aligned before saving, testing, or publishing the embedded applications grid dataset.
    const department = JSON.parse(
      JSON.stringify(this.currentEditingDept || {})
    ) as Department;
    const nextName = (this.form.get('name')?.value || this.currentEditingDept?.name || '').trim();

    if (nextName) {
      department.name = nextName;
    }

    return department;
  }

  private persistAndSyncDepartmentDataSet(
    department: Department
  ): Observable<string | null> {
    // Added by Amisha on 04/24/2026: save the dataset first, then update the department link only when the dataset id changes so publish matches the legacy department flow more closely.
    return new Observable<string | null>(observer => {
      this.persistDepartmentDataSet(department).subscribe({
        next: dataSetId => {
          if (
            !dataSetId ||
            !this.currentEditingDept ||
            this.currentEditingDept.applicationGridDataSetId === dataSetId
          ) {
            observer.next(dataSetId);
            observer.complete();
            return;
          }

          const updatedDepartment = JSON.parse(
            JSON.stringify(this.currentEditingDept)
          ) as Department;
          updatedDepartment.applicationGridDataSetId = dataSetId;

          this._clientService.saveClientDepartment(updatedDepartment).subscribe({
            next: savedDepartment => {
              this.currentEditingDept = savedDepartment;
              const departmentIndex = this.departments.findIndex(
                departmentItem => departmentItem.id === savedDepartment.id
              );

              if (departmentIndex > -1) {
                this.departments[departmentIndex] = savedDepartment;
              }

              observer.next(dataSetId);
              observer.complete();
            },
            error: error => observer.error(error)
          });
        },
        error: error => observer.error(error)
      });
    });
  }

  private getStaticFieldDefinition(
    fieldName?: string | null
  ): DataSetField | undefined {
    if (!fieldName) {
      return undefined;
    }

    return this.getProtectedStaticFieldsForDepartment().find(
      staticField => staticField.name === fieldName
    );
  }

  private getProtectedStaticFieldsForDepartment(): DataSetField[] {
    // Added by Amisha on 05/21/2026 to separate true protected/system fields
    // from default starter columns that the legacy designer still allows deleting.
    const staticFields = this.getStaticFieldsForDepartment();
    const initialFieldNames = new Set(
      this.getInitialFieldsForDepartment().map(field => field.name)
    );

    return staticFields.filter(field => !initialFieldNames.has(field.name));
  }

  closeTestResults() {
    this.resultColumns = null;
    this.resultRows = null;
  }

  private createResultTableColumns(
    columns: Array<{ prop: string; name: string }>
  ): CommonTableColumn[] {
    return columns.map(column => ({
      key: column.prop,
      label: column.name,
      headerType: 'text',
      cellType: 'text'
    }));
  }

  // -------------------------------------------------------------
  // GENERIC API TESTING CONTROLS LOGIC
  // -------------------------------------------------------------

  testGetDepartmentApplications() {
    if (!this.currentEditingDept?.id) {
      return;
    }

    this.testingApi = true;
    this.lastApiTestDescription = 'Get Department Applications (All)';
    
    this._workflowService.getDepartmentApplications(
      this.currentEditingDept.id,
      { pageNumber: 1, maxRows: 100 },
      this.getColumnsToInclude()
    ).subscribe({
      next: (result) => {
        this.testingApi = false;
        this.apiTestResults = result;
        this.apiTestResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.apiTestResultRows = result.dataSetResult?.rows || [];
        this.apiTestResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();

        this._toastr.success(`Retrieved ${result.dataSetResult?.rows?.length || 0} applications.`);
      },
      error: (error) => {
        this.testingApi = false;
        this._toastr.error(`API call failed: ${error.message || 'Unknown error'}`);
        this.clearApiTestResults();
      }
    });
  }

  testFilter(filterName: string) {
    if (!this.currentEditingDept?.id) {
      return;
    }

    this.testingFilter = filterName;
    const filterLabel = this.getFilterLabel(filterName);
    this.lastApiTestDescription = `Filter Test: ${filterLabel}`;

    let filterInput;
    if (filterName === 'MY_APPLICATIONS') {
      const currentUserId = this._context.user?.id;
      if (!currentUserId) {
        this._toastr.error('Cannot test My Applications: User ID not found');
        this.testingFilter = null;
        return;
      }

      filterInput = {
        pageNumber: 1,
        maxRows: 100,
        inputValues: [
          {
            title: 'submitter_Id',
            searchText: currentUserId,
            filterText: currentUserId,
            strOperator: '==',
            inputType: 'text'
          }
        ]
      };
    } else {
      filterInput = {
        pageNumber: 1,
        maxRows: 100,
        inputValues: [
          {
            title: filterName,
            searchText: '1',
            filterText: filterName,
            strOperator: '>',
            inputType: 'number'
          }
        ]
      };
    }

    this._workflowService.getDepartmentApplications(
      this.currentEditingDept.id,
      filterInput,
      this.getColumnsToInclude()
    ).subscribe({
      next: (result) => {
        this.testingFilter = null;
        this.apiTestResults = result;
        this.apiTestResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.apiTestResultRows = result.dataSetResult?.rows || [];
        this.apiTestResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};

        const rowCount = result.dataSetResult?.rows?.length || 0;
        this._toastr.success(`${filterLabel}: Found ${rowCount} application(s).`);
      },
      error: (error) => {
        this.testingFilter = null;
        this._toastr.error(`Filter test failed: ${error.message || 'Unknown error'}.`);
        this.clearApiTestResults();
      }
    });
  }

  getFilterLabel(filterName: string): string {
    const labels: { [key: string]: string } = {
      'REQUIRED_DOCUMENTS': 'Required Documents',
      'GENERATED_PERMITS': 'Generated Permits',
      'REGISTRATIONS_TO_RENEW': 'Registrations to Renew',
      'REGISTRATIONS_ABOUT_TO_EXPIRE': 'Registrations About to Expire',
      'INSPECTIONS': 'Upcoming Inspections',
      'INSPECTIONS_TO_SCHEDULE': 'Inspections to Schedule',
      'INSPECTIONS_TO_COMPLETE': 'Inspections to Complete',
      'UPCOMING_INSPECTIONS_TO_COMPLETE': 'Upcoming Inspections to Complete',
      'PENDING_PAYMENTS': 'Pending Payments',
      'MY_APPLICATIONS': 'My Applications',
      'SHARED_BY_ME': 'Shared By Me',
      'SHARED_WITH_ME': 'Shared With Me',
      'RELEVANT_TO_USER': 'Relevant To User',
      'NEEDS_MY_ATTENTION': 'Needs My Attention'
    };
    return labels[filterName] || filterName;
  }

  testCustomFieldFilter() {
    if (!this.currentEditingDept?.id) {
      return;
    }

    if (!this.customFilterField || !this.customFilterValue) {
      this._toastr.warning('Please select a field and enter a filter value');
      return;
    }

    this.testingCustomFilter = true;
    this.lastApiTestDescription = `Custom Filter: ${this.customFilterField} ${this.customFilterOperator} "${this.customFilterValue}"`;

    const filterInput = {
      pageNumber: 1,
      maxRows: 100,
      inputValues: [
        {
          title: this.customFilterField,
          searchText: this.customFilterValue,
          filterText: this.customFilterValue,
          strOperator: this.customFilterOperator,
          inputType: 'text'
        }
      ]
    };

    this._workflowService.getDepartmentApplications(
      this.currentEditingDept.id,
      filterInput,
      this.getColumnsToInclude()
    ).subscribe({
      next: (result) => {
        this.testingCustomFilter = false;
        this.apiTestResults = result;
        this.apiTestResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.apiTestResultRows = result.dataSetResult?.rows || [];
        this.apiTestResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();

        const rowCount = result.dataSetResult?.rows?.length || 0;
        this._toastr.success(`Found ${rowCount} application(s) with custom filter.`);
      },
      error: (error) => {
        this.testingCustomFilter = false;
        this._toastr.error(`Filter test failed: ${error.message || 'Unknown error'}.`);
        this.clearApiTestResults();
      }
    });
  }

  updateAvailableFilterFields(): void {
    if (this.columns && this.columns.length > 0) {
      this.availableFilterFields = this.columns.map(col => ({
        name: col.name,
        resultHeader: col.resultHeader,
        label: col.resultHeader || col.name
      }));
    } else {
      this.availableFilterFields = this.sortableColumns.map(col => ({
        name: col.name,
        resultHeader: col.name,
        label: col.label
      }));
    }
  }

  testSimpleSearch() {
    if (!this.currentEditingDept?.id) {
      return;
    }

    if (!this.simpleSearchValue || !this.simpleSearchValue.trim()) {
      this._toastr.warning('Please enter a search value');
      return;
    }

    this.testingSimpleSearch = true;
    this.lastApiTestDescription = `Simple Search: "${this.simpleSearchValue}"`;

    const fieldsToSearch = this.availableFilterFields.length > 0
      ? this.availableFilterFields
      : this.sortableColumns;

    const filterInput = {
      pageNumber: 1,
      maxRows: 100,
      inputValues: fieldsToSearch.map(field => ({
        title: field.name || field.resultHeader,
        searchText: '',
        filterText: this.simpleSearchValue.trim(),
        strOperator: 'contains',
        inputType: 'text',
        searchOptionMethod: 'simpleSearch'
      }))
    };

    this._workflowService.getDepartmentApplications(
      this.currentEditingDept.id,
      filterInput,
      this.getColumnsToInclude()
    ).subscribe({
      next: (result) => {
        this.testingSimpleSearch = false;
        this.apiTestResults = result;
        this.apiTestResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.apiTestResultRows = result.dataSetResult?.rows || [];
        this.apiTestResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();

        const rowCount = result.dataSetResult?.rows?.length || 0;
        this._toastr.success(`Found ${rowCount} application(s) matching "${this.simpleSearchValue}".`);
      },
      error: (error) => {
        this.testingSimpleSearch = false;
        this._toastr.error(`Simple search failed: ${error.message || 'Unknown error'}.`);
        this.clearApiTestResults();
      }
    });
  }

  testSorting(columnName: string, descending: boolean) {
    if (!this.currentEditingDept?.id) {
      return;
    }

    this.testingSortField = `${columnName}_${descending ? 'DESC' : 'ASC'}`;
    const direction = descending ? 'descending' : 'ascending';
    const columnLabel = this.sortableColumns.find(c => c.name === columnName)?.label || columnName;
    this.lastApiTestDescription = `Sort Test: ${columnLabel} (${direction})`;

    this._workflowService.getDepartmentApplications(
      this.currentEditingDept.id,
      { pageNumber: 1, maxRows: 100 },
      this.getColumnsToInclude(),
      descending,
      columnName
    ).subscribe({
      next: (result) => {
        this.testingSortField = null;
        this.apiTestResults = result;
        this.apiTestResultColumns = result.dataSet?.dataSetConfig?.fields || [];
        this.apiTestResultRows = result.dataSetResult?.rows || [];
        this.apiTestResultRowsOriginal = [...(result.dataSetResult?.rows || [])];
        this.gridSortColumn = null;
        this.gridSortDescending = false;
        this.gridFilters = {};
        this.updateAvailableFilterFields();

        const rowCount = result.dataSetResult?.rows?.length || 0;
        this._toastr.success(`Retrieved ${rowCount} application(s) sorted by ${columnLabel} (${direction}).`);
      },
      error: (error) => {
        this.testingSortField = null;
        this._toastr.error(`Sort test failed: ${error.message || 'Unknown error'}.`);
        this.clearApiTestResults();
      }
    });
  }

  getCellValue(row: any, columnName: string): any {
    let convertedKey: string;

    if (columnName.includes('_')) {
      const parts = columnName.split('_');
      convertedKey = parts.map((part, index) => {
        if (index === 0) {
          if (part.length === 0) return part;
          if (part.length === 1) return part.toLowerCase();
          return part.slice(0, -1).toLowerCase() + part.slice(-1).toUpperCase();
        } else {
          return part.toUpperCase();
        }
      }).join('_');
    } else {
      convertedKey = columnName.charAt(0).toLowerCase() + columnName.slice(1);
    }

    const cell = row?.values?.[convertedKey];
    return cell ?? '';
  }

  clearApiTestResults() {
    this.apiTestResults = null;
    this.apiTestResultColumns = [];
    this.apiTestResultRows = [];
    this.apiTestResultRowsOriginal = [];
    this.lastApiTestDescription = '';
    this.gridSortColumn = null;
    this.gridSortDescending = false;
    this.gridFilters = {};
  }

  sortGridByColumn(columnName: string) {
    if (this.gridSortColumn === columnName) {
      this.gridSortDescending = !this.gridSortDescending;
    } else {
      this.gridSortColumn = columnName;
      this.gridSortDescending = false;
    }
    this.applyGridSortAndFilter();
  }

  onGridFilterChange(columnName: string, filterValue: string) {
    if (filterValue) {
      this.gridFilters[columnName] = filterValue.toLowerCase();
    } else {
      delete this.gridFilters[columnName];
    }
    this.applyGridSortAndFilter();
  }

  applyGridSortAndFilter() {
    let filteredRows = [...this.apiTestResultRowsOriginal];

    if (Object.keys(this.gridFilters).length > 0) {
      filteredRows = filteredRows.filter(row => {
        return Object.keys(this.gridFilters).every(columnName => {
          const cellValue = this.getCellValue(row, columnName);
          const filterValue = this.gridFilters[columnName];
          return cellValue?.toString().toLowerCase().includes(filterValue);
        });
      });
    }

    if (this.gridSortColumn) {
      filteredRows.sort((a, b) => {
        const aValue = this.getCellValue(a, this.gridSortColumn!);
        const bValue = this.getCellValue(b, this.gridSortColumn!);

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        const aNum = Number(aValue);
        const bNum = Number(bValue);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return this.gridSortDescending ? bNum - aNum : aNum - bNum;
        }

        const aStr = aValue.toString().toLowerCase();
        const bStr = bValue.toString().toLowerCase();
        const comparison = aStr.localeCompare(bStr);
        return this.gridSortDescending ? -comparison : comparison;
      });
    }

    this.apiTestResultRows = filteredRows;
  }

  getSortIcon(columnName: string): string {
    if (this.gridSortColumn !== columnName) {
      return 'unfold_more'; 
    }
    return this.gridSortDescending ? 'arrow_drop_down' : 'arrow_drop_up'; 
  }

  clearGridFilters() {
    this.gridFilters = {};
    this.applyGridSortAndFilter();
  }

  initializeAvailableColumns(): void {
    if (this.columns && this.columns.length > 0) {
      this.availableColumns = this.columns.map(col => ({
        name: col.name,
        label: col.resultHeader || col.name
      }));
    } else {
      this.availableColumns = [];
    }
    this.selectedColumns = this.availableColumns.map(col => col.name);
    this.selectAllColumns = true;
  }

  toggleColumnSelection(columnName: string): void {
    const index = this.selectedColumns.indexOf(columnName);
    if (index > -1) {
      this.selectedColumns.splice(index, 1);
    } else {
      this.selectedColumns.push(columnName);
    }
    this.updateSelectAllState();
  }

  isColumnSelected(columnName: string): boolean {
    return this.selectedColumns.includes(columnName);
  }

  toggleAllColumns(): void {
    if (this.selectAllColumns) {
      this.selectedColumns = this.availableColumns.map(col => col.name);
    } else {
      this.selectedColumns = [];
    }
  }

  updateSelectAllState(): void {
    this.selectAllColumns = this.selectedColumns.length === this.availableColumns.length;
  }

  getColumnsToInclude(): string[] | undefined {
    if (this.selectAllColumns || this.selectedColumns.length === 0) {
      return undefined;
    }
    return this.selectedColumns;
  }
}
